/**
 * React Query hooks for the secrets endpoints (RLY-026).
 *
 * Standing-order [25] + Sprint-04 retro [PROCESS]: explicit `useQuery` /
 * `useMutation` generic declarations; companion `*-shape.test.ts` ships
 * `expectTypeOf` assertions to lock the inferred shapes against drift.
 *
 * Cross-package wires (per [12]):
 *   - `SecretListResponse`, `SecretCreateResponse`, `CreateSecretRequest`
 *     from `@simpliwork/api-schema` (`packages/api-schema/src/secrets.ts`).
 *   - API endpoints: `apps/portal-api/src/routes/secrets.ts:191` (POST),
 *     `:220` (GET), `:266` (DELETE) — RLY-023 producer.
 *
 * **Hard-rule headline (RLY-023):** read responses NEVER include the secret
 * value. The `SecretListResponse` shape's lack of a `value` field is enforced
 * by the schema; a structural-equivalence test in `use-secrets-shape.test.ts`
 * verifies the type-level guarantee at compile time.
 */
import { useAuth } from "@clerk/clerk-react";
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  ApiError,
  CreateSecretRequest,
  SecretCreateResponse,
  SecretListResponse,
} from "@/lib/api-client";
import { createSecret, deleteSecret, fetchSecrets } from "@/lib/api-client";

export const secretsKeys = {
  all: ["secrets"] as const,
  list: () => ["secrets", "list"] as const,
} as const;

export type SecretsListQueryKey = ReturnType<typeof secretsKeys.list>;

// ─── List ─────────────────────────────────────────────────────────────────

export type UseSecretsListResult = UseQueryResult<SecretListResponse, ApiError | Error>;

export function useSecretsList(): UseSecretsListResult {
  const { getToken } = useAuth();
  return useQuery<SecretListResponse, ApiError | Error, SecretListResponse, SecretsListQueryKey>({
    queryKey: secretsKeys.list(),
    queryFn: () => fetchSecrets({ getToken: () => getToken() }),
  });
}

// ─── Create ───────────────────────────────────────────────────────────────

export type UseCreateSecretResult = UseMutationResult<
  SecretCreateResponse,
  ApiError | Error,
  CreateSecretRequest
>;

export function useCreateSecret(): UseCreateSecretResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<SecretCreateResponse, ApiError | Error, CreateSecretRequest>({
    mutationFn: (body) => createSecret(body, { getToken: () => getToken() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: secretsKeys.all });
    },
  });
}

// ─── Delete ───────────────────────────────────────────────────────────────

export type UseDeleteSecretResult = UseMutationResult<void, ApiError | Error, { name: string }>;

export function useDeleteSecret(): UseDeleteSecretResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<void, ApiError | Error, { name: string }>({
    mutationFn: ({ name }) => deleteSecret(name, { getToken: () => getToken() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: secretsKeys.all });
    },
  });
}
